/**
 * Copyright (c) 2020 Google Inc
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of
 * this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to
 * use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
 * the Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
 * FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 * COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
 * IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
 * CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

if (location.search) {
  var a = document.createElement('a');
  a.href = location.href;
  a.search = '';
  history.replaceState(null, null, a.href);
}

function tweet_(url) {
  open(
    'https://twitter.com/intent/tweet?url=' + encodeURIComponent(url),
    '_blank',
  );
}
function tweet(anchor) {
  tweet_(anchor.getAttribute('href'));
}

function share(anchor) {
  var url = anchor.getAttribute('href');
  event.preventDefault();
  if (navigator.share) {
    navigator.share({
      url: url,
    });
  } else if (navigator.clipboard) {
    navigator.clipboard.writeText(url);
    message('Article URL copied to clipboard.');
  } else {
    tweet_(url);
  }
}

function toggleMenu(e) {
  document.body.classList.toggle('nav-open');
}

/**
 * Factory: Create a handler registry with controlled access
 * Isolates mutable state inside closure, exposes only register/resolve methods
 * Pre-populated with core event handlers: toggleMenu, share, tweet
 * @return {Object} Registry object with {register, resolve} methods
 */
const createHandlerRegistry = () => {
  // Isolated mutable state
  const handlers = new Map();

  // Pre-register core handlers (these are the ones used by HTML on-click attributes)
  handlers.set('toggleMenu', toggleMenu);
  handlers.set('share', share);
  handlers.set('tweet', tweet);

  return {
    /**
     * Register a handler function with a name
     * @param {string} name Handler name
     * @param {Function} fn Handler function
     */
    register(name, fn) {
      handlers.set(name, fn);
    },

    /**
     * Resolve and retrieve a handler by name
     * @param {string} name Handler name
     * @return {Function|undefined} Handler function or undefined if not found
     */
    resolve(name) {
      return handlers.get(name);
    }
  };
};

// Initialize the registry
const handlerRegistry = createHandlerRegistry();

function message(msg) {
  var dialog = document.getElementById('message');
  dialog.textContent = msg;
  dialog.setAttribute('open', '');
  setTimeout(function () {
    dialog.removeAttribute('open');
  }, 3000);
}

function prefetch(e) {
  if (e.target.tagName != 'A') {
    return;
  }
  if (e.target.origin != location.origin) {
    return;
  }
  /**
   * Return the given url with no fragment
   * @param {string} url potentially containing a fragment
   * @return {string} url without fragment
   */
  const removeUrlFragment = (url) => url.split('#')[0];
  if (
    removeUrlFragment(window.location.href) === removeUrlFragment(e.target.href)
  ) {
    return;
  }
  var l = document.createElement('link');
  l.rel = 'prefetch';
  l.href = e.target.href;
  document.head.appendChild(l);
}
document.documentElement.addEventListener('mouseover', prefetch, {
  capture: true,
  passive: true,
});
document.documentElement.addEventListener('touchstart', prefetch, {
  capture: true,
  passive: true,
});

const GA_ID = document.documentElement.getAttribute('ga-id');
window.ga =
  window.ga ||
  function () {
    if (!GA_ID) {
      return;
    }
    (ga.q = ga.q || []).push(arguments);
  };
ga.l = +new Date();
ga('create', GA_ID, 'auto');
ga('set', 'transport', 'beacon');
var timeout = setTimeout(
  (onload = function () {
    clearTimeout(timeout);
    ga('send', 'pageview');
  }),
  1000,
);

var ref = +new Date();
function ping(event) {
  var now = +new Date();
  if (now - ref < 1000) {
    return;
  }
  ga('send', {
    hitType: 'event',
    eventCategory: 'page',
    eventAction: event.type,
    eventLabel: Math.round((now - ref) / 1000),
  });
  ref = now;
}
addEventListener('pagehide', ping);
addEventListener('visibilitychange', ping);

/**
 * Injects a script into document.head
 * @param {string} src path of script to be injected in <head>
 * @return {Promise} Promise object that resolves on script load event
 */
const dynamicScriptInject = (src) => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = src;
    script.type = 'text/javascript';
    document.head.appendChild(script);
    script.addEventListener('load', () => {
      resolve(script);
    });
  });
};

// Script web-vitals.js will be injected dynamically if user opts-in to sending CWV data.
const sendWebVitals = document.currentScript.getAttribute('data-cwv-src');

if (/web-vitals.js/.test(sendWebVitals)) {
  dynamicScriptInject(`${window.location.origin}/js/web-vitals.js`)
    .then(() => {
      webVitals.getCLS(sendToGoogleAnalytics);
      webVitals.getFID(sendToGoogleAnalytics);
      webVitals.getLCP(sendToGoogleAnalytics);
    })
    .catch((error) => {
      console.error(error);
    });
}

addEventListener(
  'click',
  function (e) {
    var button = e.target.closest('button');
    if (!button) {
      return;
    }
    ga('send', {
      hitType: 'event',
      eventCategory: 'button',
      eventAction: button.getAttribute('aria-label') || button.textContent,
    });
  },
  true,
);
var selectionTimeout;
addEventListener(
  'selectionchange',
  function () {
    clearTimeout(selectionTimeout);
    var text = String(document.getSelection()).trim();
    if (text.split(/[\s\n\r]+/).length < 3) {
      return;
    }
    selectionTimeout = setTimeout(function () {
      ga('send', {
        hitType: 'event',
        eventCategory: 'selection',
        eventAction: text,
      });
    }, 2000);
  },
  true,
);

if (window.ResizeObserver && document.querySelector('header nav #nav')) {
  var progress = document.getElementById('reading-progress');

  /**
   * Pure function: Calculate reading progress percentage
   * @param {number} scrollTop Current scroll position
   * @param {number} docHeight Total scrollable height
   * @param {number} windowHeight Viewport height
   * @return {number} Progress percentage (0-100)
   */
  const calculateProgressPercent = (scrollTop, docHeight, windowHeight) => {
    const maxScroll = docHeight - windowHeight;
    return Math.min((scrollTop / maxScroll) * 100, 100);
  };

  /**
   * Pure function: Update scroll state immutably
   * @param {Object} currentState Current state object
   * @param {number} newScrollY New scroll Y position
   * @param {number} newWindowHeight New window height
   * @return {Object} New state object
   */
  const updateScrollState = (currentState, newScrollY, newWindowHeight) => ({
    ...currentState,
    lastScrollY: newScrollY,
    windowHeight: newWindowHeight,
  });

  /**
   * Factory: Create initial scroll state
   * @return {Object} Initial state with default values
   */
  const createScrollState = () => ({
    lastScrollY: 0,
    requestedFrame: false,
    scrollBottom: 10000,
    windowHeight: 1000,
  });

  // Imperative shell: Initialize state
  let scrollState = createScrollState();
  let updateInProgress = false;

  // Imperative shell: scroll event handler
  function scroll() {
    if (!scrollState.requestedFrame) {
      requestAnimationFrame(updateProgress);
      scrollState = { ...scrollState, requestedFrame: true };
    }
    scrollState = { ...scrollState, lastScrollY: Date.now() };
  }
  addEventListener('scroll', scroll);

  // Imperative shell: update progress and DOM
  function updateProgress() {
    if (updateInProgress) return;
    updateInProgress = true;

    scrollState = { ...scrollState, requestedFrame: false };

    // Pure calculation
    const percent = calculateProgressPercent(
      document.scrollingElement.scrollTop,
      scrollState.scrollBottom,
      scrollState.windowHeight
    );

    // Imperative DOM update
    progress.style.transform = `translate(-${100 - percent}vw, 0)`;

    // Check if we should continue animating
    if (Date.now() - scrollState.lastScrollY < 3000) {
      requestAnimationFrame(updateProgress);
      scrollState = { ...scrollState, requestedFrame: true };
    }
    updateInProgress = false;
  }

  // Imperative shell: ResizeObserver
  new ResizeObserver(() => {
    const newBottom =
      document.scrollingElement.scrollTop +
      document.querySelector('#comments,footer').getBoundingClientRect().top;
    const newHeight = window.innerHeight;

    // Update state with new dimensions
    scrollState = updateScrollState(scrollState, scrollState.lastScrollY, newHeight);
    scrollState = { ...scrollState, scrollBottom: newBottom };

    scroll();
  }).observe(document.body);
}

addEventListener('click', (e) => {
  const handler = e.target.closest('[on-click]');
  if (!handler) {
    return;
  }
  e.preventDefault();
  const name = handler.getAttribute('on-click');
  const fn = handlerRegistry.resolve(name);
  if (!fn) {
    throw new Error('Unknown handler: ' + name);
  }
  fn(handler);
});

function removeBlurredImage(img) {
  // Ensure the browser doesn't try to draw the placeholder when the real image is present.
  img.style.backgroundImage = 'none';
}
document.body.addEventListener(
  'load',
  (e) => {
    if (e.target.tagName != 'IMG') {
      return;
    }
    removeBlurredImage(e.target);
  },
  /* capture */ 'true',
);
for (let img of document.querySelectorAll('img')) {
  if (img.complete) {
    removeBlurredImage(img);
  }
}

/**
 * Injects a stylesheet into document.head
 * @param {string} src path of stylesheet to be injected in <head>
 * @return {Promise} Promise object that resolves on link load event
 */
const dynamicCssInject = (src) => {
  return new Promise((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.type = 'text/css';
    link.href = src;
    document.head.appendChild(link);
    link.addEventListener('load', () => {
      resolve(link);
    });
  });
};

/**
 * Callbacks for dynamically loaded modules
 * Pure functions that initialize features when their dependencies load
 */
const moduleCallbacks = {
  /**
   * Initialize basic gallery lightbox (for .gallery elements)
   */
  initGallery: () => {
    const options = {};
    const lightbox = GLightbox({ ...options });
  },

  /**
   * Initialize product gallery with interaction (for .product-gallery elements)
   * Also loads the gallery interaction script
   */
  initProductGallery: () => {
    const productLightbox = GLightbox({
      selector: '.glightbox',
      touchNavigation: true,
      loop: true,
      autoplayVideos: false,
    });
    // Load gallery interaction script
    dynamicScriptInject(`${window.location.origin}/js/gallery-interact.js`).catch(
      (error) => {
        console.error('Failed to load gallery-interact.js:', error);
      }
    );
  },
};

/**
 * Factory: Create a dynamic modules loader
 * Handles conditional script/CSS loading based on page content
 * @param {Array} config Array of module configurations
 * @param {Object} callbacks Map of callback names to functions
 * @return {Function} Loader function
 */
const createDynamicModulesLoader = (config, callbacks) => {
  return () => {
    config.forEach(({ selector, js, css, thenJs, thenCss }) => {
      // Skip if selector not found in DOM
      if (!document.querySelector(selector)) return;

      // Load JavaScript
      if (js) {
        dynamicScriptInject(js)
          .then(() => {
            // Call registered callback if name provided
            if (thenJs && callbacks[thenJs]) {
              callbacks[thenJs]();
            }
          })
          .catch((error) => {
            console.error(`Failed to load ${js}:`, error);
          });
      }

      // Load CSS
      if (css) {
        dynamicCssInject(css)
          .then(() => {
            // Call registered callback if name provided
            if (thenCss && callbacks[thenCss]) {
              callbacks[thenCss]();
            }
          })
          .catch((error) => {
            console.error(`Failed to load ${css}:`, error);
          });
      }
    });
  };
};

// Import dynamic modules config
import { dynamicModulesConfig } from './dynamic-modules.config.js';

// Initialize and register loader
const loadDynamicModules = createDynamicModulesLoader(dynamicModulesConfig, moduleCallbacks);

// Execute on page load
window.addEventListener('load', loadDynamicModules);

/**
 * YOUTUBE
 *
 * HTML ALTERNATE
 * https://css-tricks.com/lazy-load-embedded-youtube-videos/
 */
window.addEventListener('load', (e) => {
  let YouTubeContainers = document.querySelectorAll('.embed-youtube');

  // Iterate over every YouTube container you may have
  for (let i = 0; i < YouTubeContainers.length; i++) {
    let container = YouTubeContainers[i];
    let imageSource =
      'https://img.youtube.com/vi/' +
      container.dataset.videoId +
      '/sddefault.jpg';

    // Load the Thumbnail Image asynchronously
    let image = new Image();
    image.src = imageSource;
    image.addEventListener('load', function () {
      container.appendChild(image);
    });

    // When the user clicks on the container, load the embedded YouTube video
    container.addEventListener('click', function () {
      let iframe = document.createElement('iframe');

      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute(
        'allow',
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
      );
      // Important: add the autoplay GET parameter, otherwise the user would need to click over the YouTube video again to play it
      iframe.setAttribute(
        'src',
        'https://www.youtube.com/embed/' +
          this.dataset.videoId +
          '?rel=0&showinfo=0&autoplay=1',
      );

      // Clear Thumbnail and load the YouTube iframe
      this.innerHTML = '';
      this.appendChild(iframe);
    });
  }
});

/**
 * WHATSAPP BUTTON
 *
 * The fixed WhatsApp button overlaps the footer's own contact links,
 * so hide it while the footer is in view.
 */
const whatsappButtons = document.querySelectorAll('.whatsapp');
const footerContainer = document.querySelector('footer .container');
if (whatsappButtons.length && footerContainer && window.IntersectionObserver) {
  new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      whatsappButtons.forEach((button) => {
        button.classList.toggle('is-hidden', entry.isIntersecting);
      });
    });
  }).observe(footerContainer);
}

/**
 * SCROLL REVEAL
 *
 * Elements marked [data-reveal] fade/slide in once they enter the viewport.
 * CSS in utility/animation.scss handles the actual transition and the
 * prefers-reduced-motion fallback; this just toggles the trigger class.
 */
const revealTargets = document.querySelectorAll('[data-reveal]');
if (revealTargets.length && window.IntersectionObserver) {
  const revealObserver = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -40px 0px' },
  );
  revealTargets.forEach((el) => revealObserver.observe(el));
} else {
  revealTargets.forEach((el) => el.classList.add('is-visible'));
}
